import { AuthenticatedService } from "../abstract/base.service";
import { ListResponse } from "@/models/grids/list.response";
import {
  EditActivityCommand,
  CreateActivityCommand,
  ChangeActivityStatusCommand,
  EditActivityMetadataCommand,
  StartActivityLogtimeWorkCommand,
  PauseActivityLogtimeWorkCommand,
  CompleteActivityLogtimeWorkCommand
} from "@/server/app.commands";
import {
  ActivityDto,
  ActivityLogtimeWorkDto,
  ActivityStatisticsDto,
  MetadataConfiguration
} from "@/server/app.dtos";
import { FilesUploader } from "@/models/uploaders/file.uploader";
import { ResultUploader } from "@/models/uploaders/result.uploader";
import { UploaderService } from "@/services/cdn/uploader.service";
import {
  GetActivityDtoQuery,
  GetActivitiesDtosQuery,
  GetActivityStatisticsDtoQuery,
  GetActivityMetadataDtoQuery,
  GetLastActivityLogtimeWorkDtoQuery
} from "@/server/app.queries";

export const ActivitiesEndpoint = `api/activities`;

export class ActivitiesService extends AuthenticatedService {
  GetList(request: GetActivitiesDtosQuery): Promise<ListResponse<ActivityDto>> {
    return this.Get<ListResponse<ActivityDto>>(
      `${this.baseUrl}${ActivitiesEndpoint}?${this.GetQueryStringByObject(
        request
      )}`
    );
  }

  GetSingle(request: GetActivityDtoQuery): Promise<ActivityDto> {
    return this.Get<ActivityDto>(
      `${this.baseUrl}${ActivitiesEndpoint}/${
        request.idActivity
      }?${this.GetQueryStringByObject(request)}`
    );
  }

  GetStatistics(
    request: GetActivityStatisticsDtoQuery
  ): Promise<ActivityStatisticsDto> {
    return this.Get<ActivityStatisticsDto>(
      `${this.baseUrl}${ActivitiesEndpoint}/${request.idActivity}/statistics`
    );
  }

  Create(command: CreateActivityCommand): Promise<number> {
    return this.Post<number>(`${this.baseUrl}${ActivitiesEndpoint}`, command);
  }

  Edit(command: EditActivityCommand): Promise<number> {
    return this.Put<number>(
      `${this.baseUrl}${ActivitiesEndpoint}/${command.idActivity}`,
      command
    );
  }

  DeleteById(id: number): Promise<number> {
    return this.Delete(`${this.baseUrl}${ActivitiesEndpoint}/${id}`);
  }

  ChangeStatus(command: ChangeActivityStatusCommand): Promise<number> {
    return this.Put(
      `${this.baseUrl}${ActivitiesEndpoint}/${command.idActivity}/status/${command.idActivityStatus}`,
      command
    );
  }

  UploadFiles(rawData: any[]): Promise<ResultUploader[]> {
    const uploader = new FilesUploader();
    uploader.folder = "/Activities/";
    uploader.files = rawData;
    return new UploaderService().UploadMultipleFiles(uploader);
  }

  GetMetadata(
    request: GetActivityMetadataDtoQuery
  ): Promise<MetadataConfiguration> {
    return this.Get<MetadataConfiguration>(
      `${this.baseUrl}${ActivitiesEndpoint}/${
        request.idActivity
      }/metadata?${this.GetQueryStringByObject(request)}`
    );
  }

  EditMetadata(command: EditActivityMetadataCommand): Promise<number> {
    return this.Put<number>(
      `${this.baseUrl}${ActivitiesEndpoint}/${command.idActivity}/metadata`,
      command
    );
  }

  GetLastLogtime(
    request: GetLastActivityLogtimeWorkDtoQuery
  ): Promise<ActivityLogtimeWorkDto> {
    return this.Get<ActivityLogtimeWorkDto>(
      `${this.baseUrl}${ActivitiesEndpoint}/${request.idActivity}/last-logtimework`
    );
  }

  StartLogtime(command: StartActivityLogtimeWorkCommand): Promise<number> {
    return this.Post<number>(
      `${this.baseUrl}${ActivitiesEndpoint}/${command.idActivity}/start-logtimework`,
      command
    );
  }

  PauseLogtime(command: PauseActivityLogtimeWorkCommand): Promise<number> {
    return this.Post<number>(
      `${this.baseUrl}${ActivitiesEndpoint}/${command.idActivity}/pause-logtimework`,
      command
    );
  }

  CompleteLogtime(
    command: CompleteActivityLogtimeWorkCommand
  ): Promise<number> {
    return this.Post<number>(
      `${this.baseUrl}${ActivitiesEndpoint}/${command.idActivity}/complete-logtimework`,
      command
    );
  }
}
